import Image from 'next/image';

import { P3 } from '@repo/ui/components/Typography';
import { hideScrollBar } from '@repo/ui/components/utilities';
import { QUERIES } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';
import styled from 'styled-components';

import { ScrollButtonBase } from 'components/utilities/styles';
import { FlexColumn, FlexRow } from 'styles/layouts';

import { HORIZONTAL_PADDING_MOBILE } from '../../../utilities/pageStyles';

import { UseCaseSectionProps } from './types';

type Scale = NonNullable<UseCaseSectionProps['scale']>;

export const CardsWrapper = styled.div`
  position: relative;
  max-width: 100%;
`;

type ListType = {
  scale: Scale;
};

const getPaddingLaptopAndUp = ({ scale }: ListType) =>
  `12px ${HORIZONTAL_PADDING_MOBILE} ${scale === 'small' ? '24px' : '20px'} 0`;

export const List = styled(FlexRow)<ListType>`
  width: 100%;
  position: relative;
  overflow-x: scroll;
  gap: 10px;

  ${QUERIES.laptopAndUp} {
    gap: 10px;
    padding: ${getPaddingLaptopAndUp};
  }

  margin: 0 0;
  padding: 24px ${HORIZONTAL_PADDING_MOBILE} 20px;
  ${hideScrollBar}
`;

type ScrollButtonType = {
  align: 'left' | 'right';
  scale: Scale;
};

const getAlignment = ({ align }: ScrollButtonType) => `${align}: -1px;`;
const getScale = ({ scale }: ScrollButtonType) =>
  `${scale === 'small' ? '-1px' : HORIZONTAL_PADDING_MOBILE};`;

export const ScrollButton = styled(ScrollButtonBase)<ScrollButtonType>`
  top: 42%;
  ${getAlignment}
  ${getScale}
  ${QUERIES.laptopAndUp} {
    ${getAlignment}
  }
`;

type CardItemType = {
  scale: Scale;
  isActive: boolean;
};

const getPadding = ({ scale }: CardItemType) =>
  scale === 'small' ? '8px' : '10px';
const getBackground = ({ isActive }: CardItemType) =>
  isActive ? themeStyle.varBackgroundAlt : '';
const getBoxShadow = ({ isActive }: CardItemType) =>
  isActive ? themeStyle.varShadowSoft : '';
const getBorderRadius = ({ scale }: CardItemType) =>
  scale === 'small' ? themeStyle.radiusMedium : themeStyle.radiusLarge;
const getRowGap = ({ scale }: CardItemType) =>
  scale === 'small' ? '6px' : '10px';

export const CardItem = styled(FlexColumn)<CardItemType>`
  padding: ${getPadding};
  background: ${getBackground};
  box-shadow: ${getBoxShadow};
  border-radius: ${getBorderRadius};
  row-gap: ${getRowGap};

  cursor: pointer;
  text-align: center;
  transition: transform 0.3s;

  @media (hover: hover) {
    &:hover {
      background: ${themeStyle.varBackgroundAlt};
      box-shadow: ${themeStyle.varShadowSoft};
    }
  }
`;

export const cardPreviewDimensions = ({ scale }: { scale: Scale }): string =>
  scale === 'small' ? '100px' : '150px';

export const CardPreview = styled.div<{ scale: Scale }>`
  display: block;
  position: relative;

  width: 90px;
  height: 90px;

  ${QUERIES.tabletAndUp} {
    width: ${cardPreviewDimensions};
    height: ${cardPreviewDimensions};
  }
`;

export const OverlayText = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  opacity: 0;
  transition: 0.2s ease;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: ${themeStyle.radiusSmall};

  @media (hover: hover) {
    ${CardItem}:hover & {
      opacity: 1;
    }
  }
`;

export const CardImage = styled(Image).withConfig({
  shouldForwardProp: () => true,
})`
  border-radius: ${themeStyle.radiusSmall};
`;

export const UseCaseText = styled(P3)`
  color: ${themeStyle.varInkMedium};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 90px;

  ${QUERIES.tabletAndUp} {
    width: 100%;
  }
`;
